import CButton from "components/CButton";
import { Fragment, useState, useEffect } from "react";
import {
  Box,
  useTheme,
  Skeleton,
  Container,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CLoader from "components/CLoader";
import EditLoanAmount from "./EditLoanAmount";
import useTypeOfCreditLine from "./useTypeOfCreditLine";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TypeOfCreditLineOptions from "./TypeOfCreditLineOptions";
import useUser from "context/UserCustomer/useUserCustomerContext";
import ConfirmTXNotHomestead from "components/CTracker/StepsDialogs/ConfirmTXNotHomestead";

const TypeOfCreditLine: React.FC = () => {
  const typeOfCreditLine = useTypeOfCreditLine();
  const theme = useTheme();
  const { user } = useUser();
  const loan = typeOfCreditLine.loan;
  const property = loan?.property;
  const [editLoanAmountOpen, setEditLoanAmountOpen] = useState(false);
  const txNotAHomestead = loan?.borrowerFlags?.txHomestead === false;
  const loanNotArchived = !loan?.borrowerFlags?.archiveType;
  const [openModalConfirmTx, setOpenModalConfirmTx] = useState(txNotAHomestead);
  useEffect(() => {
    setOpenModalConfirmTx(txNotAHomestead);
  }, [txNotAHomestead]);

  const isFirstLien =
    loan?.initialOffer.currentLoanBalance === 0 &&
    loan.occupancy === "Primary Residence";
  //temporary TN validation
  const tnMaxOffer =
    loan?.property?.address?.components.state_abbreviation === "TN"
      ? Math.floor(loan?.initialOffer?.tnMaxOffer ?? 0)
      : undefined;

  return (
    <Fragment>
      <CLoader loading={typeOfCreditLine && user && property ? false : true} />
      <Container
        maxWidth="md"
        disableGutters={useMediaQuery(theme.breakpoints.only("xs"))}
      >
        <Box>
          <Typography
            mt={4}
            mb={5}
            variant="h1"
            lineHeight={1}
            color={theme.palette.text.primary}
            letterSpacing={-0.8}
            px={{ xs: 2, md: 0 }}
            fontSize={{ xs: "2rem", md: "3rem" }}
          >
            <span
              style={{ color: theme.palette.primary.main, marginRight: "10px" }}
            >
              Congrats {user?.firstName}!{" "}
            </span>{" "}
            You've been pre-qualified for a HELOC for up to{" "}
            <span style={{ color: theme.palette.primary.main }}>
              $
              {tnMaxOffer //temporary TN validation
                ? tnMaxOffer.toLocaleString("en-US")
                : Math.floor(loan?.initialOffer?.maxOffer ?? 0).toLocaleString(
                    "en-US",
                  )}
            </span>
            !{!isFirstLien && " View your options below"}.
          </Typography>
        </Box>

        <Box
          mb={0.5}
          px={{ xs: 2, md: 6 }}
          py={{ xs: 1, md: 4 }}
          bgcolor={theme.palette.background.paper}
          sx={{ borderTopLeftRadius: 34, borderTopRightRadius: 34 }}
        >
          <Box py={{ xs: 2, md: 0 }}>
            <Typography fontWeight={200} sx={{ opacity: 0.9 }}>
              Subject Property
            </Typography>
            {property?.address?.delivery_line_1 ? (
              <Typography
                variant="h5"
                color={theme.palette.text.primary}
                fontFamily={"Pro Display Bold"}
              >
                {property?.address?.delivery_line_1}.{" "}
                {property?.address?.components?.city_name},{" "}
                {property?.address?.components?.state_abbreviation}.{" "}
                {property?.address?.components?.zipcode}
              </Typography>
            ) : (
              <Skeleton width="75%" height={"1.5rem"} />
            )}
          </Box>
        </Box>

        <Box
          mb={2}
          px={{ xs: 2, md: 6 }}
          py={{ xs: 1, md: 3 }}
          bgcolor={theme.palette.background.paper}
          sx={{ borderBottomLeftRadius: 34, borderBottomRightRadius: 34 }}
        >
          <Box py={{ xs: 2, md: 0 }}>
            <Box
              sx={{
                backgroundImage:
                  "linear-gradient(to right, lightgray 50%, transparent 50%)",
                backgroundPosition: "0 75%",
                backgroundRepeat: "repeat-x",
                backgroundSize: "8px 1px",
              }}
              display="flex"
              alignItems={"center"}
              justifyContent={"space-between"}
              fontFamily="Pro Display Semibold"
            >
              <Box
                minWidth={100}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <Typography variant={"h6"} fontFamily={"Pro Display Regular"}>
                  Loan Request
                </Typography>
              </Box>
              <Box
                display={"flex"}
                alignSelf={"stretch"}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <Box
                  gap={1}
                  minWidth={140}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography variant={"h5"} fontFamily={"Pro Display Bold"}>
                    $
                    {Math.floor(loan?.initialOffer?.amount ?? 0).toLocaleString(
                      "en-US",
                    )}
                  </Typography>

                  <CButton
                    sx={{ minHeight: 0, minWidth: 0, padding: 0 }}
                    onClick={() => setEditLoanAmountOpen(true)}
                  >
                    Edit
                  </CButton>
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography
            variant="h6"
            color={theme.palette.text.secondary}
            textAlign={"justify"}
            fontFamily={"Pro Display Light"}
            mt={{ xs: 0, md: 2 }}
          >
            The following steps will be required to finalize your loan:
          </Typography>
          <Box
            display={"flex"}
            mt={1.5}
            mb={{ xs: 1.5, md: 0 }}
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent={"left"}
            alignItems={"left"}
          >
            <Typography
              mr={1.5}
              variant={"h6"}
              display={"flex"}
              color={theme.palette.text.primary}
              alignItems={"center"}
              justifyContent={{ xs: "left", md: "center" }}
              fontFamily={"Pro Display Regular"}
            >
              <CheckCircleIcon
                style={{ fontSize: 30, marginRight: 4 }}
                htmlColor={theme.palette.primary.main}
              />
              ID Verification
            </Typography>
            <Typography
              mr={1.5}
              variant={"h6"}
              display={"flex"}
              color={theme.palette.text.primary}
              alignItems={"center"}
              justifyContent={{ xs: "left", md: "center" }}
              fontFamily={"Pro Display Regular"}
            >
              <CheckCircleIcon
                style={{ fontSize: 30, marginRight: 4 }}
                htmlColor={theme.palette.primary.main}
              />
              Valuation Report
            </Typography>
            <Typography
              mr={1.5}
              variant={"h6"}
              display={"flex"}
              color={theme.palette.text.primary}
              alignItems={"center"}
              justifyContent={{ xs: "left", md: "center" }}
              fontFamily={"Pro Display Regular"}
            >
              <CheckCircleIcon
                style={{ fontSize: 30, marginRight: 4 }}
                htmlColor={theme.palette.primary.main}
              />
              Income Verification
            </Typography>
          </Box>
        </Box>
        {loan && <TypeOfCreditLineOptions loan={loan} />}
      </Container>
      <EditLoanAmount
        endpoint="/change-offer-amount/initial"
        open={editLoanAmountOpen}
        isFirstLien={isFirstLien}
        tnMaxOffer={tnMaxOffer} //temporary TN validation
        loanId={loan?.id}
        onSubmit={() => {
          typeOfCreditLine.handleRefresh();
        }}
        onClose={() => {
          setEditLoanAmountOpen(false);
        }}
        state={property?.address?.components?.state_abbreviation}
        setEditLoanAmountOpen={setEditLoanAmountOpen}
      />
      {openModalConfirmTx && loanNotArchived && (
        <ConfirmTXNotHomestead
          open={openModalConfirmTx}
          onAccept={() => {
            setOpenModalConfirmTx(false);
          }}
        />
      )}
    </Fragment>
  );
};

export default TypeOfCreditLine;
